<template>
  <div
    class="ns-product-card"
    :class="{ shadow: !noAction && !isTouchDevice }"
    itemscope
    itemtype="http://schema.org/Product"
    :data-sku="sku"
    @mouseenter="openAction"
    @mouseleave="closeAction"
  >
    <!-- Favorite Icon -->
    <div class="ns-product-card__favorite" @click.stop="handleFavoriteClick">
      <NsIcon
        v-if="!isBaseSku && !hideFavorites"
        class="ns-product-card__favorite-icon"
        :class="{ favorited: isFavorite }"
        :icon-name="
          isFavorite ? 'icon-favorite-heart-hollow' : 'icon-favorite-heart'
        "
      />
    </div>

    <div class="ns-product-card__body" @click="handleCardClick">
      <!-- OFFER & SALES -->
      <div
        v-if="isExclusive && !hideExclusiveIcon"
        class="ns-product-card__exclusive-title"
      >
        <NsIcon
          class="ns-product-card__exclusive-icon"
          icon-name="icon-exclusive-offer"
        />
        <span :class="{ 'hide-text-mobile': hideExclusiveTextOnMobile }">
          {{ translations.exclusiveOfferTitle }}
        </span>
      </div>
      <div v-if="isSale && salesLabel" class="ns-product-card__sales-label">
        {{ salesLabel }}
      </div>

      <!-- IMAGE -->
      <NsImage
        class="ns-product-card__image"
        :src="image.src"
        :alt="image.alt"
        :fallback-src="imageFallbackSrc"
        maintain-ratio="1x1"
        image-fit="contain"
        size-lock="2"
        itemprop="image"
      />

      <!-- TITLE -->
      <!-- eslint-disable-next-line -->
      <div class="ns-product-card__title" itemprop="name" v-html="title" />

      <!-- SKU & POINTS -->
      <NsProductSkuPoints
        v-if="showSkuPrices"
        :forcesv="forcesv"
        :hide-sku="hideSku || noAction"
        :is-distributor="isDistributor"
        :points="points"
        :sku="sku"
        :translations="translations"
      />

      <!-- PRICES -->
      <NsProductPrice
        v-if="showPrices"
        :is-base-sku="isBaseSku"
        :min-price="minPrice"
        :max-price="maxPrice"
        :original-price="originalPrice"
        :price="price"
        :price-label="priceLabel"
        :sale-active="isSale"
        :show-original-crossed-out="showOriginalCrossedOut"
      />
    </div>

    <!-- VARIANT SELECTOR -->
    <NsProductVariantSelector
      v-if="showVariantSelector && !noAction"
      class="ns-product-card__variant-selector"
      :placeholder="translations.variantDropdownPlaceholder"
      :selected-sku="isBaseSku ? '' : sku"
      :disabled="disabled"
      :variant-skus="variantSkus"
      :variants="variants"
      @select="handleSelectVariant"
    />

    <!-- ACTION -->
    <div
      class="ns-product-card__action"
      :class="{
        static: isTouchDevice,
        'shadow-action': !isTouchDevice
      }"
    >
      <NsAnimateHeight
        v-if="!noAction"
        :open="actionOpened || isTouchDevice || showAction"
      >
        <NsProductCardAction
          :disable-add-to-adr="disableAddToAdr"
          :disable-add-to-cart="disableAddToCart"
          :disable-quantity-selector="disableQuantitySelector"
          :exclusive-offer-message="exclusiveOfferMessage"
          :max-quantity="maxQuantity"
          :quantity="quantity"
          :show-exclusive-offer-message="showExclusiveOfferMessage"
          :status-message="statusMessage"
          :status-message-key="statusMessageKey"
          :translations="translations"
          @quantity-select="handleQuantitySelect"
          @add-to-cart="handleAddToCart"
          @add-to-adr="handleAddToAdr"
          @go-to-details="handleCardClick"
        />
      </NsAnimateHeight>
    </div>
  </div>
</template>

<script>
import { NsIcon, NsAnimateHeight } from "@nuskin/design-components";
import NsImage from "./NsImage.vue";
import NsProductCardAction from "./NsProductCardAction.vue";
import NsProductPrice from "./NsProductPrice.vue";
import NsProductSkuPoints from "./NsProductSkuPoints.vue";
import NsProductVariantSelector from "./NsProductVariantSelector.vue";

/**
 * NsProductCardBase
 *
 * How to use: You shouldn't use this component. Use NsProductCard instead. It
 * includes the Product mixin. This component is simply all the html and styles.
 *
 * @event variant-select  When a variant selection occurs, this event emits a
 * function with params: (value, index)
 * @event quantity-select  When a quantity selection occurs, this event emits a
 * function with params: (value, index)
 * @event add-to-cart  When clicking on "Add to Cart", this event emits a
 * function with params: ()
 * @event add-to-adr  When clicking on "Add to Adr", this event emits a function
 * with params: ()
 * @event card-click  When clicking on the ProductCard, this event emits a
 * function with params: (product). If listener is NOT assigned, clicking the
 * card will go to the product detail page
 * @event favorite-click  When clicking on the favorite icon, this event emits a
 * function with params: ()
 */

export default {
  name: "NsProductCardBase",
  components: {
    NsIcon,
    NsImage,
    NsProductPrice,
    NsProductCardAction,
    NsAnimateHeight,
    NsProductSkuPoints,
    NsProductVariantSelector
  },
  props: {
    disableAddToAdr: Boolean,
    disableAddToCart: Boolean,
    disableQuantitySelector: Boolean,
    exclusiveOfferMessage: {
      type: String,
      default: ""
    },
    forcesv: Boolean,
    hideExclusiveIcon: Boolean,
    hideExclusiveTextOnMobile: Boolean,
    hideFavorites: Boolean,
    hideSku: Boolean,
    // TODO: src/alt/fallbackSrc props to image prop
    imageFallbackSrc: {
      type: String,
      default: ""
    },
    image: {
      type: Object,
      default() {
        return {};
      }
    },
    imageSet: {
      type: Array,
      default() {
        return [];
      }
    },
    isBaseSku: Boolean,
    isDistributor: Boolean,
    isExclusive: Boolean,
    isFavorite: Boolean,
    isLoggedIn: Boolean,
    isLoyaltyEnabled: Boolean,
    isSale: Boolean,
    loyaltyStatusAndLinks: {
      type: Object,
      default() {
        return {};
      }
    },
    marketAttributes: {
      type: Object,
      default() {
        return {};
      }
    },
    maxQuantity: {
      type: Number,
      default: null
    },
    maxPrice: {
      type: String,
      default: ""
    },
    minPrice: {
      type: String,
      default: ""
    },
    noAction: Boolean,
    points: {
      type: String,
      default: null
    },
    originalPrice: {
      type: String,
      default: ""
    },
    price: {
      type: Number,
      default: null
    },
    quantity: {
      type: String,
      default: "1"
    },
    salesLabel: {
      type: String,
      default: ""
    },
    /**
     * This prop is only for storybook for simple dev use
     */
    showAction: Boolean,
    showExclusiveOfferMessage: Boolean,
    showOriginalCrossedOut: Boolean,
    showVariantSelector: Boolean,
    sku: {
      type: String,
      default: ""
    },
    /**
     * Warning message, if product is out of stock or whatever
     */
    statusMessage: {
      type: String,
      default: ""
    },
    /**
     * Warning message key, for qa testing automation
     */
    statusMessageKey: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    },
    translations: {
      type: Object,
      default() {
        return {};
      }
    },
    userIsQualified: Boolean,
    userLoyalty: {
      type: Object,
      default() {
        return {};
      }
    },
    variantSkus: {
      type: Array,
      default() {
        return [];
      }
    },
    variants: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      actionOpened: false,
      initSku: null,
      isStatusSet: false,
      isTouchDevice: this.isMobileDevice(),
      product: null,
      variantIndex: null
    };
  },
  computed: {
    showPrices() {
      return (
        ((this.price && this.price !== "0") ||
          (this.minPrice && this.minPrice !== "0")) &&
        !(this.isExclusive && !this.userIsQualified && !this.noAction)
      );
    },
    showSkuPrices() {
      return !this.isBaseSku && !this.noAction;
    }
  },
  methods: {
    closeAction() {
      this.actionOpened = false;
    },
    handleQuantitySelect(value, index) {
      /**
       * Fired when a new quantity is selected: function(value: string|number,
       * index: number) => void
       *
       * @type {select}
       * @property {(string|number)} value data-value attribute of the selected
       * item, or index
       * @property {number} index index of the selected item
       */
      this.$emit("quantity-select", value, index);
    },
    handleAddToCart(event) {
      /**
       * Fired when "add to cart" button is clicked: function(event) => void
       *
       * @type {click}
       * @property {event} event the click event
       */
      this.$emit("add-to-cart", event);
    },
    handleAddToAdr(event) {
      /**
       * Fired when "add to cart" button is clicked: function(event) => void
       *
       * @type {click}
       * @property {event} event the click event
       */
      this.$emit("add-to-adr", event);
    },
    handleSelectVariant(selected, index) {
      /**
       * Fired when a new variant is selected: function(value: string|number,
       * index: number) => void
       *
       * @type {select}
       * @property {(string|number)} value data-value attribute of the selected
       * item, or index
       * @property {number} index index of the selected item
       */
      this.$emit("variant-select", selected, index);
    },
    /**
     * Checks if device is mobile (will use modal if so)
     */
    isMobileDevice() {
      return (
        typeof window.orientation !== "undefined" ||
        navigator.userAgent.includes("IEMobile")
      );
    },
    handleCardClick() {
      this.$emit("card-click");
    },
    handleFavoriteClick() {
      this.$emit("favorite-click");
    },
    openAction() {
      this.actionOpened = true;
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';

* {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  color: map.get(colors.$themeColors, color-interactive-7);
}

.ns-product-card {
  position: relative;
  // Not sure why these styles for mobile
  // margin-left: 20px;
  // margin-right: 20px;
  // padding: 30px 0px 20px;
  // adding desktop styles here
  margin-left: 0;
  margin-right: 0;
  padding: 30px 20px 20px;
  // height: 100%;
  min-width: 230px;
  background-color: white;
  @include global.p-small;
  text-align: center;
  transition: box-shadow 0.3s ease-in-out;
  // @include global.ns-product-card;
  cursor: pointer;

  &.shadow:hover {
    box-shadow: rgba(0, 0, 0, 0.15) 0 8px 8px 0;
  }
}
.ns-product-card__favorite {
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 10px;
  width: 40px;
  height: 40px;
}

.ns-product-card__favorite-icon {
  fill: white;
  transition: map.get(other.$otherUtils, global-transition);

  &.favorited {
    fill: map.get(colors.$baseColors, mermaid);
    color: map.get(colors.$baseColors, mermaid);
  }
}

.ns-product-card__body {
  display: grid;
  grid-gap: 10px;
  width: 100%;
}

.ns-product-card__sales-label {
  position: absolute;
  top: 10px;
  left: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 5px 0;
  height: 20px;
  color: white;
  background-color: map.get(colors.$themeColors, color-interactive-7);
  @include global.subtitle-small-bold-caps;
  letter-spacing: 1px;
}

.ns-product-card__exclusive-title {
  position: absolute;
  top: 10px;
  display: flex;
  align-items: center;
  color: map.get(colors.$themeColors, color-interactive-7);
  font-size: 13px;
  font-weight: 300;
  line-height: 12px;
  text-align: center;
}

.ns-product-card__exclusive-icon {
  margin-bottom: 4px;
  margin-left: 16px;
  height: 20px;
  width: 40px;
}

.ns-product-card__sales-label::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 100%;
  border-top: 10px solid map.get(colors.$themeColors, color-interactive-7);
  border-bottom: 10px solid map.get(colors.$themeColors, color-interactive-7);
  border-right: 6px solid transparent;
  width: 0;
  font-size: 0;
  line-height: 0;
}

.ns-product-card__image {
  display: block;
  margin: 0 auto;
  width: 100%;
  max-width: 165px;
}

.ns-product-card__title {
  padding-bottom: 2px;
  max-height: 60px;
  @include global.title-small;
  overflow: hidden;
}

.ns-product-card__title sup {
  font-size: inherit;
  line-height: 1;
  vertical-align: top;
}

.ns-product-card__variant-selector {
  margin-top: 10px;
}

.ns-product-card__action {
  user-select: none;
  z-index: map.get(other.$otherUtils, z-primary);
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  width: 100%;

  &.static {
    position: static;
    width: calc(100% + 40px);
    margin: 20px -20px -20px;
  }

  &.shadow-action {
    box-shadow: rgba(0, 0, 0, 0.15) 0 8px 8px 0;
  }
}

@media (max-width: 767px) {
  .hide-text-mobile {
    display: none;
  }
}

@media (min-width: 1024px) {
  .ns-product-card {
    min-width: 280px;
  }
  // Added these styles above
  // .ns-product-card {
  //   margin-left: 0;
  //   margin-right: 0;
  //   padding: 30px 20px 20px;
  // }
}

div:empty,
span:empty {
  display: none;
}
</style>
